import { getBaseUrl } from "redux/utils";
import { baseApi } from "../../redux/baseAPI";
import axios from "axios";
import { cloneDeep } from "lodash";
import store from "redux/store";
import moment from "moment";

export const problemApi = baseApi.injectEndpoints({
  tagTypes: ["Problems"],
  endpoints: (builder) => ({
    getProblems: builder.query({
      query: (patientId) => {
        return {
          url: `/problems/index.json`,
          method: "GET",
          params: { pid: patientId, filter: "all" },
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
      providesTags: ['Problems'],
    }),
    getProblemsMeta: builder.query({
      query: (patientId) => {
        return {
          url: `/ccm/ccm_meta_list.json`,
          method: "GET",
          params: { pid: patientId, type: "problems" },
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getDeleteFavourite: builder.mutation({
      query: (body) => {
        return {
          url: `/labs/delete_fav.json`,
          method: "GET",
          params: {
            pid: body.pid,
            code: body.code,
            type: "clinical",
            audit_encounter_id: body.audit_encounter_id,
          },
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    fetchFavorites: builder.query({
      query: (patientId) => {
        return {
          url: `/ccm/get_health_conditions.json`,
          method: "GET",
          params: { pid: patientId, filter: "all" },
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "FavProblems", id })),
              { type: "FavProblems", id: "LIST" },
            ]
          : [{ type: "FavProblems", id: "LIST" }],
    }),
    updateProblem: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/ccm/add_patient_health_conditions.json`,
        method: "POST",
        data: body,
        params: {
          pid: body.pid,
          skip_auto_accept: body.skip_auto_accept ? 1 : 0,
        },
      }),
      transformResponse: (response, meta, arg) => response,
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          problemApi.util.updateQueryData("getProblems", id, (draft) => {
            Object.assign(draft, patch);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["Problems"],
    }),
    updateFavourite: builder.mutation({
      query: (body) => {
        return {
          url: `/labs/add_favorite.json`,
          method: "POST",
          data: body[0],
          params: {
            pid: body[1].pid,
            audit_encounter_id: body[1].audit_encounter_id,
          },
        };
      },
      invalidatesTags: ["PatientNotes"],
      transformResponse: (response, meta, arg) => response.response,
    }),
    addProblem: builder.mutation({
      query: (payload) => {
        return {
          url: `/problems/add.json`,
          method: "POST",
          data: { ...payload },
        };
      },
      transformResponse: (response, meta, arg) => response,
      invalidatesTags: [{type: "Problems"},{type: 'history', id: 'medical_history'},"SystemSuggestedInterventions","QualityMeasureQuestions"],
    }),
    bulkUpdateProblemStatus: builder.mutation({
      query: (payload) => {
        return {
          url: `/ccm/bulk_update_health_condition_status.json`,
          method: "POST",
          data: { ...payload },
        };
      },
      transformResponse: (response, meta, arg) => response,
      invalidatesTags: [
        { type: "Problems" },
        { type: "history", id: "medical_history" },
        "SystemSuggestedInterventions",
        "QualityMeasureQuestions",
      ],
    }),
    addPmedHx: builder.mutation({
      query: (payload) => {
        return {
          url: `/histories/add_past_medical_history.json?pid=${payload.pid}`,
          method: "POST",
          data: { ...payload },
        };
      },
      transformResponse: (response, meta, arg) => response,
      invalidatesTags: ["history"],
    }),
    updatePmedHx: builder.mutation({
      query: (payload) => {
        return {
          url: `/histories/update_past_medical_history.json?pid=${payload.pid}`,
          method: "POST",
          data: { ...payload },
        };
      },
      transformResponse: (response, meta, arg) => response,
      invalidatesTags: ["history"],
    }),
    fetchFavoriteLabs: builder.query({
      query: (body) => {
        let apiParams = cloneDeep(body);
        if (apiParams && typeof apiParams?.pid !== 'undefined' && apiParams?.pid) {
          // skipping pid from fav list api to fetch all fav records, because for few patients its not returning all the fav icds
          delete apiParams.pid;
        }
        return {
          url: `/labs/list_favorite.json`,
          method: "GET",
          params: apiParams
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
      providesTags:['FavoriteProb']
      // {
      //   return (response.response.code == 0 ? response.response.data.map((item)=> { return {label: item.short_desc}}) : [])
      // }
    }),
    deleteFavoriteProb: builder.mutation({
      query: (body) => {
        return {
          url: `/labs/delete_fav.json`,
          method: "GET",
          params: body
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
      invalidatesTags: ["FavoriteProb"],

    }),
    addFavoriteProb: builder.mutation({
      query: (body) => {
        return {
          url: `/labs/add_favorite.json`,
          method: "POST",
          data: body
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
      invalidatesTags: ["FavoriteProb"],

    }),
    updateAPProblem: builder.mutation({
      query: (body) => {
        return {
          url: `/notes/update_ap_problem_detail.json`,
          method: "POST",
          data: body
        };
      },
      transformResponse: (response, meta, arg) => response.response,
      invalidatesTags: ["Problems"],

    }),
    
  }),
});

export const {
  useGetProblemsQuery,
  useGetProblemsMetaQuery,
  useGetDeleteFavouriteMutation,
  useFetchFavoritesQuery,
  useUpdateProblemMutation,
  useUpdateFavouriteMutation,
  useAddProblemMutation,
  useBulkUpdateProblemStatusMutation,
  useFetchFavoriteLabsQuery,
  useDeleteFavoriteProbMutation,
  useAddFavoriteProbMutation,
  useAddPmedHxMutation,
  useUpdatePmedHxMutation,
  useUpdateAPProblemMutation
} = problemApi;

export function searchProblems(inputValue) {
  const currentState = store?.getState();
  let DOS = currentState?.providerNote?.selectedNote?.encounter_data?.date;
  if(DOS){
    DOS = moment(DOS).format("YYYY-MM-DD");
  }
  else{
    DOS = "";
  }
  return axios.get(
    `${getBaseUrl()}/ccm/get_health_conditions_icds.json?keyword=${inputValue}&dos=${DOS}`
  );
}
export function searchICD(payload) {
  let url = `${getBaseUrl()}/utilities/search_icd10.json?`;

  if (payload.keyword) {
    url += `keyword=${payload.keyword}&`;
  }
  if (payload.dos) {
    url += `dos=${payload.dos}&`;
  }
  if (payload.pid) {
    url += `pid=${payload.pid}&`;
  }

  return axios.get(url);
}
